import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where
} from 'firebase/firestore';
import { db } from '../protected/firebase';
import { useAuth } from '../../../contexts/AuthContext';

export default function ProductView() {
  const { user, companyId } = useAuth();
  const [productTypes, setProductTypes] = useState([]);
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(false);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [endModalOpen, setEndModalOpen] = useState(false);
  const [openingCash, setOpeningCash] = useState(null);
  const [closingCash, setClosingCash] = useState(null);
  const [startSaving, setStartSaving] = useState(false);
  const [endSaving, setEndSaving] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [closingSummary, setClosingSummary] = useState(null);
  const [loadingClosingSummary, setLoadingClosingSummary] = useState(false);
  const navigate = useNavigate();

  const denominations = useMemo(
    () => ([
      { key: 'rm1', label: 'RM 1', value: 1 },
      { key: 'rm5', label: 'RM 5', value: 5 },
      { key: 'rm10', label: 'RM 10', value: 10 },
      { key: 'rm20', label: 'RM 20', value: 20 },
      { key: 'rm50', label: 'RM 50', value: 50 },
      { key: 'rm100', label: 'RM 100', value: 100 },
      { key: 'c1', label: '1¢', value: 0.01 },
      { key: 'c5', label: '5¢', value: 0.05 },
      { key: 'c10', label: '10¢', value: 0.1 },
      { key: 'c20', label: '20¢', value: 0.2 },
      { key: 'c50', label: '50¢', value: 0.5 }
    ]),
    []
  );

  const emptyCash = useMemo(() => {
    return denominations.reduce((acc, d) => ({ ...acc, [d.key]: 0 }), {});
  }, [denominations]);

  const formatCurrency = (val) => `RM ${Number(val || 0).toFixed(2)}`;

  const getTodayKey = () => new Date().toISOString().split('T')[0];

  const calculateCashTotal = (cash) => {
    if (!cash) return 0;
    return denominations.reduce((sum, d) => sum + (Number(cash[d.key] || 0) * d.value), 0);
  };

  const parseTimestamp = (ts) => {
    if (!ts) return null;
    if (typeof ts.toDate === 'function') {
      return ts.toDate();
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    const typesRef = collection(db, 'companies', companyId, 'companySettings', 'inventory', 'productTypes');
    const unsub = onSnapshot(
      typesRef,
      (snapshot) => {
        const types = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setProductTypes(types);
      },
      (error) => {
        console.error('Error fetching product types:', error);
      }
    );
    return () => unsub();
  }, [user, companyId]);

  const loadSession = async () => {
    if (!companyId) return;
    setLoadingSession(true);
    try {
      const sessionRef = doc(db, 'companies', companyId, 'cashSessions', getTodayKey());
      const snap = await getDoc(sessionRef);
      if (snap.exists()) {
        setSession({ id: snap.id, ...snap.data() });
      } else {
        setSession(null);
      }
    } catch (error) {
      console.error('Failed to load cash session:', error);
      setSessionError('Unable to load start/end of day status.');
    } finally {
      setLoadingSession(false);
    }
  };

  useEffect(() => {
    loadSession();
  }, [companyId]);

  const handleClick = (type) => {
    navigate('/cashier', { state: { selectedType: type } });
  };

  const handleStartSave = async () => {
    if (!companyId || !user) return;
    setStartSaving(true);
    try {
      const sessionRef = doc(db, 'companies', companyId, 'cashSessions', getTodayKey());
      const openingTotal = calculateCashTotal(openingCash);
      const payload = {
        dateKey: getTodayKey(),
        startedAt: new Date().toISOString(),
        startedBy: user.uid,
        openingCash,
        openingTotal,
        endedAt: null,
        endedBy: null,
        closingCash: null,
        closingTotal: 0,
        cashSalesTotal: 0,
        cashVariance: 0,
        productSales: [],
        bundleSales: []
      };
      await setDoc(sessionRef, payload);
      setSession(payload);
      setStartModalOpen(false);
      setSessionError('');
    } catch (error) {
      console.error('Failed to start day:', error);
      setSessionError('Unable to start day. Please try again.');
    } finally {
      setStartSaving(false);
    }
  };

  const aggregateSales = (orders) => {
    const productMap = {};
    const bundleMap = {};

    orders.forEach((order) => {
      (order.items || []).forEach((item) => {
        const qty = Number(item.qty ?? item.quantity ?? 1) || 0;
        const price = Number(item.price || 0);
        const lineTotal = qty * price;
        const type = item.type || 'product';

        if (type === 'bundle') {
          if (!bundleMap[item.id]) {
            bundleMap[item.id] = { id: item.id, name: item.name || 'Bundle', qty: 0, total: 0 };
          }
          bundleMap[item.id].qty += qty;
          bundleMap[item.id].total += lineTotal;
        } else {
          if (!productMap[item.id]) {
            productMap[item.id] = { id: item.id, name: item.name || 'Product', qty: 0, total: 0, variants: {} };
          }
          productMap[item.id].qty += qty;
          productMap[item.id].total += lineTotal;

          if (item.variantId || item.variantName) {
            const variantKey = item.variantId || item.variantName;
            if (!productMap[item.id].variants[variantKey]) {
              productMap[item.id].variants[variantKey] = {
                id: item.variantId || variantKey,
                name: item.variantName || 'Variant',
                qty: 0,
                total: 0
              };
            }
            productMap[item.id].variants[variantKey].qty += qty;
            productMap[item.id].variants[variantKey].total += lineTotal;
          }
        }
      });
    });

    const products = Object.values(productMap).map((p) => ({
      ...p,
      variants: Object.values(p.variants || {})
    }));
    const bundles = Object.values(bundleMap);
    return { products, bundles };
  };

  const loadClosingSummary = async () => {
    if (!companyId || !session?.startedAt) return;
    setLoadingClosingSummary(true);
    try {
      const start = parseTimestamp(session.startedAt) || new Date(`${getTodayKey()}T00:00:00.000Z`);
      const end = new Date();
      const ordersRef = collection(db, 'companies', companyId, 'orders');
      const q = query(
        ordersRef,
        orderBy('timestamp'),
        where('timestamp', '>=', start.toISOString()),
        where('timestamp', '<=', end.toISOString())
      );
      const snap = await getDocs(q);
      const orders = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const normalizePayment = (pm = '') => pm.toString().toLowerCase();
      const normalizeStatus = (status = '') => status.toString().toLowerCase();

      const nonVoidOrders = orders.filter((o) => normalizeStatus(o.paymentStatus) !== 'void');
      const voidCount = orders.length - nonVoidOrders.length;

      const cashOrders = nonVoidOrders.filter((o) => normalizePayment(o.paymentMethod) === 'cash');
      const cardOrders = nonVoidOrders.filter((o) => {
        const m = normalizePayment(o.paymentMethod);
        return m.includes('card') || m.includes('debit') || m.includes('credit');
      });
      const ewalletOrders = nonVoidOrders.filter((o) => normalizePayment(o.paymentMethod).includes('wallet'));

      const cashSalesTotal = cashOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
      const cardSalesTotal = cardOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);
      const ewalletSalesTotal = ewalletOrders.reduce((sum, o) => sum + Number(o.total || 0), 0);

      // Itemized sales across all non-void orders
      const { products, bundles } = aggregateSales(nonVoidOrders);
      setClosingSummary({
        cashSalesTotal,
        cardSalesTotal,
        ewalletSalesTotal,
        voidCount,
        products,
        bundles
      });
      setSessionError('');
    } catch (error) {
      console.error('Failed to load closing summary:', error);
      setSessionError('Unable to prepare end-of-day data. Please try again.');
    } finally {
      setLoadingClosingSummary(false);
    }
  };

  const handleEndSave = async () => {
    if (!companyId || !user || !session || !closingSummary) return;
    setEndSaving(true);
    try {
      const closingTotal = calculateCashTotal(closingCash);
      const cashSalesTotal = closingSummary.cashSalesTotal || 0;
      const variance = closingTotal - (Number(session.openingTotal || 0) + cashSalesTotal);
      const sessionRef = doc(db, 'companies', companyId, 'cashSessions', getTodayKey());
      const update = {
        endedAt: new Date().toISOString(),
        endedBy: user.uid,
        closingCash,
        closingTotal,
        cashSalesTotal,
        cardSalesTotal: closingSummary.cardSalesTotal || 0,
        ewalletSalesTotal: closingSummary.ewalletSalesTotal || 0,
        voidCount: closingSummary.voidCount || 0,
        cashVariance: variance,
        productSales: closingSummary.products,
        bundleSales: closingSummary.bundles
      };
      await updateDoc(sessionRef, update);
      setSession((prev) => ({ ...prev, ...update }));
      setEndModalOpen(false);
      setSessionError('');
    } catch (error) {
      console.error('Failed to close day:', error);
      setSessionError('Unable to close day. Please try again.');
    } finally {
      setEndSaving(false);
    }
  };

  const sessionStatus = () => {
    if (loadingSession) return 'Loading session...';
    if (!session) return 'No session started';
    if (session.endedAt) return 'Day closed';
    return 'Day in progress';
  };

  const renderCashInputs = (cashState, setCashState) => {
    const notes = denominations.filter((d) => d.value >= 1);
    const coins = denominations.filter((d) => d.value < 1);

    const renderGroup = (label, list) => (
      <div className="space-y-2">
        <p className="text-sm font-semibold text-gray-200">{label}</p>
        <div className="grid grid-cols-2 gap-3">
          {list.map((d) => (
            <label key={d.key} className="text-sm text-gray-300 space-y-1">
              <span>{d.label}</span>
              <input
                type="number"
                min="0"
                className="w-full bg-[#1f1f1f] rounded-lg px-3 py-2 text-white"
                value={cashState?.[d.key] ?? 0}
                onChange={(e) => {
                  const val = Math.max(0, parseInt(e.target.value || '0', 10));
                  setCashState((prev) => ({ ...(prev || {}), [d.key]: val }));
                }}
              />
            </label>
          ))}
        </div>
      </div>
    );

    return (
      <div className="space-y-4">
        {renderGroup('Notes', notes)}
        {renderGroup('Coins', coins)}
        <div className="text-right text-sm text-gray-400">
          Total: <span className="font-semibold text-white">{formatCurrency(calculateCashTotal(cashState))}</span>
        </div>
      </div>
    );
  };

  const renderSalesTable = (title, rows, includeVariants = false) => {
    if (!rows || rows.length === 0) return null;
    return (
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-white">{title}</h4>
        <div className="bg-[#111] border border-gray-800 rounded-lg divide-y divide-gray-800">
          {rows.map((row) => (
            <div key={row.id} className="p-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-200">{row.name}</span>
                <span className="text-gray-200">Qty: {row.qty} · {formatCurrency(row.total)}</span>
              </div>
              {includeVariants && row.variants?.length > 0 && (
                <div className="mt-2 ml-3 space-y-1">
                  {row.variants.map((variant) => (
                    <div key={variant.id} className="flex justify-between text-xs text-gray-400">
                      <span>{variant.name}</span>
                      <span>Qty: {variant.qty} · {formatCurrency(variant.total)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen text-white p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
        <h1 className="text-3xl font-bold">Select Product Category</h1>
        <div className="flex gap-3">
          <button
            onClick={() => {
              setOpeningCash(emptyCash);
              setStartModalOpen(true);
            }}
            disabled={!!session && !session.endedAt}
            className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Start of Day
          </button>
          <button
            onClick={async () => {
              if (!session || session.endedAt) return;
              setClosingCash(emptyCash);
              await loadClosingSummary();
              setEndModalOpen(true);
            }}
            disabled={!session || !!session?.endedAt}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold"
          >
            End of Day
          </button>
        </div>
      </div>

      {(!session || session?.endedAt) && (
        <div className="mb-6 bg-[#0f0f0f] border border-gray-800 rounded-xl p-4">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-sm text-gray-400">Start/End of Day</p>
              <p className="text-lg font-semibold text-white">{sessionStatus()}</p>
            </div>
            <div className="text-right text-sm text-gray-400">
              {session?.startedAt && (
                <div>Started: {new Date(session.startedAt).toLocaleString()}</div>
              )}
              {session?.endedAt && (
                <div>Ended: {new Date(session.endedAt).toLocaleString()}</div>
              )}
            </div>
          </div>
          {session && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-4 text-sm">
              <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                <p className="text-gray-400">Opening Cash</p>
                <p className="text-white font-semibold">{formatCurrency(session.openingTotal)}</p>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                <p className="text-gray-400">Closing Cash</p>
                <p className="text-white font-semibold">{session.closingTotal ? formatCurrency(session.closingTotal) : '-'}</p>
              </div>
              <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                <p className="text-gray-400">Variance</p>
                <p className="text-white font-semibold">
                  {session.endedAt ? formatCurrency(session.cashVariance || 0) : '-'}
                </p>
              </div>
            </div>
          )}
          {sessionError && <p className="text-red-400 text-sm mt-3">{sessionError}</p>}
          {session?.endedAt && (
            <p className="text-sm text-gray-400 mt-3">
              Day closed. View the detailed statement in Reports → End of Day Statements.
            </p>
          )}
        </div>
      )}

      {/* Wrap layout that adjusts card size based on text length */}
      <div className="flex flex-wrap gap-4">
        {/* "All Products" card */}
        <div
          onClick={() => handleClick(null)}
          className="cursor-pointer bg-purple-700 hover:bg-purple-800 transition-colors px-6 py-4 rounded-xl text-center"
        >
          <h2 className="text-lg font-semibold whitespace-nowrap">All Products</h2>
        </div>
        <div
          onClick={() => handleClick('bundle')}
          className="cursor-pointer bg-[#1f1f1f] hover:bg-purple-600 transition-colors px-6 py-4 rounded-xl text-center"
        >
          <h2 className="text-lg font-semibold whitespace-nowrap">Bundles</h2>
        </div>

        {/* Other product types */}
        {productTypes.map((type) => (
          <div
            key={type.id}
            onClick={() => handleClick(type.name)}
            className="cursor-pointer bg-[#1f1f1f] hover:bg-purple-600 transition-colors px-6 py-4 rounded-xl text-center"
          >
            <h2 className="text-lg font-semibold whitespace-nowrap">{type.name}</h2>
          </div>
        ))}
      </div>

      {startModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4">Start of Day - Cash on Hand</h3>
            {renderCashInputs(openingCash, setOpeningCash)}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setStartModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleStartSave}
                disabled={startSaving}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50"
              >
                {startSaving ? 'Saving...' : 'Save Start of Day'}
              </button>
            </div>
          </div>
        </div>
      )}

      {endModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
          <div className="bg-[#0f0f0f] border border-gray-800 rounded-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-semibold text-white mb-4">End of Day - Count Cash</h3>
            {loadingClosingSummary ? (
              <p className="text-gray-300 text-sm">Loading cash sales...</p>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400 text-sm">Opening Cash</p>
                    <p className="text-white font-semibold">{formatCurrency(session?.openingTotal)}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400 text-sm">Current Closing Total</p>
                    <p className="text-white font-semibold">{formatCurrency(calculateCashTotal(closingCash))}</p>
                  </div>
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400 text-sm">Voided Orders Today</p>
                    <p className="text-white font-semibold">{closingSummary?.voidCount || 0}</p>
                  </div>
                </div>
                {renderCashInputs(closingCash, setClosingCash)}
                <div className="mt-4 space-y-3">
                  {renderSalesTable('Products Sold (All)', closingSummary?.products, true)}
                  {renderSalesTable('Bundles Sold (All)', closingSummary?.bundles)}
                  <div className="bg-[#111] border border-gray-800 rounded-lg p-3">
                    <p className="text-gray-400 text-sm">Variance Preview</p>
                    <p className="text-white font-semibold">
                      {formatCurrency(calculateCashTotal(closingCash) - (Number(session?.openingTotal || 0) + Number(closingSummary?.cashSalesTotal || 0)))}
                    </p>
                  </div>
                </div>
              </>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setEndModalOpen(false)}
                className="px-4 py-2 rounded-lg bg-gray-700 text-white"
              >
                Cancel
              </button>
              <button
                onClick={handleEndSave}
                disabled={loadingClosingSummary || endSaving}
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
              >
                {endSaving ? 'Saving...' : 'Save End of Day'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
